import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import { FullScreenIconAlt } from '@src/svgs/FullScreenIcon';
import GearIcon from '@src/svgs/GearIcon';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import SpeakerIcon from '@src/svgs/SpeakerIcon';
import SpeakerMutedIcon from '@src/svgs/SpeakerMutedIcon';
import YouTubeCCIcon from '@src/svgs/YouTubeCCIcon';
import YouTubeIcon from '@src/svgs/YouTubeIcon';
import YouTubeMiniplayerIcon from '@src/svgs/YouTubeMiniplayerIcon';
import YouTubeTheaterIcon from '@src/svgs/YouTubeTheaterIcon';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { BrandName, StoryTitle } from '../BrandSlots';
import { formatPlaybackTime } from '../formatPlaybackTime';
import { useChromeFontSize } from '../useChromeFontSize';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.youtubeLongFormVideo;

/**
 * YouTube Long-Form Video overlay — paints YouTube's standard desktop
 * watch-page player chrome over the clip-preview rect: a top title strip
 * (story title + channel handle pill + "▶ YouTube" link bug), a thin red
 * progress bar, and the bottom controls bar (play/pause, volume, time
 * readout, autoplay pill, CC, settings, miniplayer, theater, fullscreen).
 *
 * No "next video" button — YouTube only paints that affordance when the
 * viewer is inside a playlist queue, which doesn't apply to a single
 * preview.
 *
 * `16:9` only. YouTube Shorts (`9:16`) is owned by a separate registration.
 *
 * Scaling: chrome is sized in `em` against a base font-size derived from
 * the rect dimensions. Reference rect **640×360** → 1em = 10px. Scale
 * factor `min(rect.width / 640, rect.height / 360)`, clamped `[0.5, 3]`.
 *
 * Chrome layering rationale: the `showPlayControls` flag gates the entire
 * top title strip + bottom progress bar + bottom controls bar together —
 * mirrors YouTube's actual auto-hide behavior where all chrome appears /
 * disappears as one group on hover. Captions stay anchored at a fixed
 * offset regardless of `showPlayControls` so the placement doesn't reflow
 * when chrome toggles.
 */
const REFERENCE_WIDTH = 640;
const REFERENCE_HEIGHT = 360;
const REFERENCE_FONT_PX = 10;
// Floor at 0.5 so chrome stays readable on small previews; ceil at 3 so it
// doesn't blow up at full-window mounts. Same shape as LinkedIn Feed Post,
// shifted down 0.1 because the YouTube reference rect is wider, so the
// effective scale at typical preview sizes runs higher.
const SCALE_MIN = 0.5;
const SCALE_MAX = 3;

export const YouTubeLongFormVideoOverlay: React.FC<SafeZoneProps> = ({
  rect,
  variants,
  onToggleVariant,
  playback,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_WIDTH,
    referenceHeight: REFERENCE_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  const showControls = !!variants.showPlayControls;
  const showCaptions = !!variants.showCaptions;

  // Live playback mirroring. When the bridge is absent (e.g. previewing in
  // a surface without a connected renderer), fall back to inert defaults —
  // the chrome still renders, it just doesn't reflect or drive a video.
  const isPlaying = playback?.isPlaying ?? false;
  const duration = playback?.duration ?? 0;
  const isReady = playback?.isReady ?? false;

  // Drag-and-seek for the progress bar. Shared with both LinkedIn overlays
  // via this hook so seek behavior — drag continuity, seek-time precedence,
  // unmount cleanup — stays identical across overlays.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);

  const togglePlayPause = () => {
    if (!playback || !isReady) return;
    if (isPlaying) playback.pause();
    else playback.play();
  };

  // Autoplay pill — visual-only local state. Toggling does nothing to the
  // renderer; YouTube's actual autoplay is a session preference at YT.com.
  const [autoplayOn, setAutoplayOn] = useState(true);

  // ---- Volume state -------------------------------------------------------
  // Defaults to **fully on / 1** so the chrome reads as "ready to play with
  // sound" — the same default state YouTube paints on a fresh watch-page
  // load. The Creatomate `Renderer` doesn't expose a volume API, so dragging
  // the rail only updates local state (visual feedback for creators
  // inspecting how the chrome behaves at intermediate volumes); clicking the
  // speaker toggles the mute visual and snaps the slider to 0 when muted.
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const volumeRailRef = useRef<HTMLDivElement | null>(null);
  // Holds the current drag's window listeners so a mid-drag unmount can
  // detach them — same pattern as `useScrubberDrag`. Without this, a
  // mid-drag modal close leaks listeners + sets state on an unmounted
  // component.
  const volumeDragListenersRef = useRef<{
    onMove: (ev: MouseEvent) => void;
    onUp: (ev: MouseEvent) => void;
  } | null>(null);
  useEffect(() => {
    return () => {
      const active = volumeDragListenersRef.current;
      if (!active) return;
      window.removeEventListener('mousemove', active.onMove);
      window.removeEventListener('mouseup', active.onUp);
      volumeDragListenersRef.current = null;
    };
  }, []);

  const onVolumeRailMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const setFromX = (clientX: number) => {
      const el = volumeRailRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width <= 0) return;
      const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      setVolume(pct);
      // Dragging up un-mutes, dragging back to 0 mutes — mirrors
      // YouTube's slider/mute coupling.
      setIsMuted(pct === 0);
    };
    setFromX(e.clientX);
    const onMove = (ev: MouseEvent) => setFromX(ev.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      volumeDragListenersRef.current = null;
    };
    volumeDragListenersRef.current = { onMove, onUp };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onMuteClick = () => {
    // YouTube's mute-button behavior: clicking when unmuted goes to muted
    // and snaps the slider to 0; clicking when muted unmutes and restores
    // a sensible volume. We don't keep the pre-mute level around, so
    // unmuting from 0 jumps to a default of 1.
    if (isMuted) {
      setIsMuted(false);
      if (volume === 0) setVolume(1);
    } else {
      setIsMuted(true);
      setVolume(0);
    }
  };

  // The chrome stays `pointer-events: none` so clicks pass through to the
  // underlying renderer. CSS `:hover` won't fire on the play button — drive
  // the dim→bright transition ourselves by checking the cursor against its
  // bounding rect on every mousemove. Same pattern LinkedIn Feed Post uses.
  const playBtnRef = useRef<HTMLButtonElement | null>(null);
  const [playHovered, setPlayHovered] = useState(false);
  useEffect(() => {
    if (!showControls) return;
    const onMove = (e: MouseEvent) => {
      const el = playBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inside =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom;
      setPlayHovered(prev => (prev === inside ? prev : inside));
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [showControls]);

  return (
    <Frame
      data-testid="youtube-long-form-video-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      {showControls && (
        <TopStrip>
          <TopTitle>
            <StoryTitle fallback={COPY.titleFallback} />
          </TopTitle>
          <TopRight>
            <ChannelHandlePill>
              @<BrandName />
            </ChannelHandlePill>
            <WatchPill>
              <YouTubeIcon width="1.6em" height="1.2em" fillColor="#ff0000" />
              <span>{COPY.watchOnYoutubeLabel}</span>
            </WatchPill>
          </TopRight>
        </TopStrip>
      )}

      {/* Caption — always mounted, fades in/out via opacity */}
      <CaptionAnchor $controlsVisible={showControls} $visible={showCaptions}>
        <CaptionBox>
          <CaptionLines>{COPY.captionPlaceholder}</CaptionLines>
        </CaptionBox>
      </CaptionAnchor>

      {showControls && (
        <ProgressBarRail
          ref={scrubberRef}
          role="slider"
          aria-label={COPY.aria.seek}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, duration)}
          aria-valuenow={Math.max(0, displayTime)}
          onMouseDown={onScrubberMouseDown}
          $interactive={scrubberInteractive}
        >
          <ProgressFill style={{ width: `${progressPct}%` }}>
            <ProgressThumb />
          </ProgressFill>
        </ProgressBarRail>
      )}

      {showControls && (
        <ControlsBar>
          <ControlsInner>
            <PlayBtn
              ref={playBtnRef}
              type="button"
              onClick={togglePlayPause}
              aria-label={isPlaying ? COPY.aria.pause : COPY.aria.play}
              aria-pressed={isPlaying}
              disabled={!playback || !isReady}
              $hovered={playHovered}
            >
              {isPlaying ? (
                <MuxPauseIcon width="1.8em" height="1.8em" />
              ) : (
                <MuxPlayIcon width="1.8em" height="1.8em" />
              )}
            </PlayBtn>

            <VolumeWrap>
              <VolumeBtn
                type="button"
                onClick={onMuteClick}
                aria-label={isMuted ? COPY.aria.unmute : COPY.aria.mute}
                aria-pressed={isMuted}
              >
                {isMuted ? (
                  <SpeakerMutedIcon
                    width="2em"
                    height="2em"
                    fillColor="#ffffff"
                    strokeColor="#ffffff"
                  />
                ) : (
                  <SpeakerIcon
                    width="2em"
                    height="2em"
                    fillColor="#ffffff"
                    strokeColor="#ffffff"
                  />
                )}
              </VolumeBtn>
              <VolumeRail
                ref={volumeRailRef}
                role="slider"
                aria-label={COPY.aria.volumeSlider}
                aria-valuemin={0}
                aria-valuemax={1}
                aria-valuenow={volume}
                onMouseDown={onVolumeRailMouseDown}
              >
                <VolumeFill style={{ width: `${volume * 100}%` }} />
                <VolumeThumb
                  style={{ left: `calc(${volume * 100}% - 0.5em)` }}
                />
              </VolumeRail>
            </VolumeWrap>

            <Time>
              {formatPlaybackTime(displayTime)} / {formatPlaybackTime(duration)}
            </Time>

            <RightCluster>
              <AutoplayTrack
                type="button"
                onClick={() => setAutoplayOn(o => !o)}
                aria-label={COPY.aria.autoplayToggle}
                aria-pressed={autoplayOn}
                $on={autoplayOn}
              >
                <AutoplayThumb $on={autoplayOn} />
              </AutoplayTrack>

              <CCButton
                type="button"
                onClick={() => onToggleVariant?.('showCaptions')}
                aria-pressed={showCaptions}
                aria-label={COPY.aria.toggleCaptions}
                $active={showCaptions}
              >
                <YouTubeCCIcon
                  width="2em"
                  height="1.4em"
                  strokeColor={showCaptions ? '#0f0f0f' : '#ffffff'}
                />
              </CCButton>

              <ControlIconBtn aria-label={COPY.aria.settings}>
                <GearIcon width="1.7em" height="1.7em" color="#ffffff" />
              </ControlIconBtn>

              <ControlIconBtn aria-label={COPY.aria.miniplayer}>
                <YouTubeMiniplayerIcon
                  width="1.8em"
                  height="1.4em"
                  strokeColor="#ffffff"
                />
              </ControlIconBtn>

              <ControlIconBtn aria-label={COPY.aria.theater}>
                <YouTubeTheaterIcon
                  width="1.8em"
                  height="1.4em"
                  strokeColor="#ffffff"
                />
              </ControlIconBtn>

              <ControlIconBtn aria-label={COPY.aria.fullscreen}>
                <FullScreenIconAlt fill="#ffffff" />
              </ControlIconBtn>
            </RightCluster>
          </ControlsInner>
        </ControlsBar>
      )}
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 640×360 reference).
// ---------------------------------------------------------------------------

const Frame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  font-family:
    'Roboto',
    system-ui,
    -apple-system,
    sans-serif;
  line-height: 1;
  color: #ffffff;
`;

// Top title strip — auto-hide group with the bottom controls. Gradient
// fades downward so the title text stays readable against bright frames
// while the rest of the video remains visible.
const TopStrip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4em;
  padding: 0.8em 1.2em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(0, 0, 0, 0.35) 60%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const TopTitle = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1.4em;
  font-weight: 600;
  letter-spacing: 0.005em;
  text-shadow: 0 0.05em 0.2em rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6em;
  flex: 0 0 auto;
`;

// Low-opacity rounded pill — channel handle. Mirrors YouTube's hover
// metadata rendering on the watch page (compact `@channel` chip in the
// upper-right area of the player when the chrome is summoned).
const ChannelHandlePill = styled.span`
  font-size: 1em;
  font-weight: 500;
  padding: 0.3em 0.8em;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1em;
  text-shadow: 0 0.05em 0.2em rgba(0, 0, 0, 0.5);
  white-space: nowrap;
`;

// "▶ YouTube" link-bug pill — visual-only, mirrors YouTube's embed-player
// external-link bug in the upper-right corner.
const WatchPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  font-size: 1em;
  font-weight: 500;
  padding: 0.3em 0.8em;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 1em;
  text-shadow: 0 0.05em 0.2em rgba(0, 0, 0, 0.5);
  white-space: nowrap;
`;

// Caption anchor — bottom offset tracks `showPlayControls` to mirror
// YouTube's real reflow.
//
// Controls off → bottom: 0.75em (0.75em above the frame edge).
// Controls on  → bottom: 5.7em. Math: progress bar top sits at
//                bottom: 4em + height: 0.4em = 4.4em from the frame
//                bottom, plus a 1.3em gap so the caption breathes
//                clear of the heavier visual mass of the controls bar.
//
// Horizontal anchor: `left: 22%` matches the x-coordinate where YouTube's
// caption text actually starts; captions read left-to-right within the
// `left: 22% / right: 22%` box, with per-line clone backgrounds painting
// inside.
const CaptionAnchor = styled.div<{
  $controlsVisible: boolean;
  $visible: boolean;
}>`
  position: absolute;
  left: 22%;
  right: 22%;
  bottom: ${({ $controlsVisible }) => ($controlsVisible ? '5.7em' : '0.75em')};
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition:
    bottom 200ms ease-out,
    opacity 200ms ease-out;
`;

// Caption text container — no background of its own (the inline
// `CaptionLines` paints the per-line pills). Left-aligned so reading order
// matches YouTube's real layout.
const CaptionBox = styled.div`
  display: block;
  color: #ffffff;
  font-size: 1.6em;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.005em;
  text-align: left;
`;

// Inline span with `box-decoration-break: clone` — each visual line wraps
// with its own tight black pill, so line backgrounds vary in width with
// each line's text length. Mirrors YouTube's actual caption rendering and
// matches the LinkedIn overlay pattern.
const CaptionLines = styled.span`
  display: inline;
  background: rgba(8, 8, 8, 0.75);
  padding: 0.2em 0.4em;
  border-radius: 0.2em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

// Thin red rail — YouTube's iconic progress bar. Sits flush above the
// controls bar. Hover-grows from `0.4em → 0.6em` for a fatter hit target
// during seeking; the inner fill keeps the same red.
const ProgressBarRail = styled.div<{ $interactive: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4em;
  height: 0.4em;
  background: rgba(255, 255, 255, 0.2);
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: height 100ms ease-out;

  &:hover {
    height: 0.6em;
  }
`;

const ProgressFill = styled.div`
  position: relative;
  height: 100%;
  background: #ff0000;
`;

// White scrub circle — visible only on hover (CSS `:hover` works here
// because the rail itself opts into pointer-events when interactive).
const ProgressThumb = styled.div`
  position: absolute;
  right: -0.6em;
  top: 50%;
  transform: translateY(-50%) scale(0);
  width: 1.2em;
  height: 1.2em;
  background: #ff0000;
  border-radius: 50%;
  transition: transform 100ms ease-out;

  ${ProgressBarRail}:hover & {
    transform: translateY(-50%) scale(1);
  }
`;

// 40px-tall gradient strip at the video bottom. Layout/sizing of the inner
// controls lives in `ControlsInner` so the bar's height stays anchored to
// the parent rect while the controls themselves can be scaled
// independently.
const ControlsBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.6) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  color: #ffffff;
`;

const ControlsInner = styled.div`
  height: 100%;
  padding: 0 1.2em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  font-size: 1.2em;
`;

// Dim by default; brighten on cursor hover (driven by JS, see component
// effect — chrome is `pointer-events: none` so CSS `:hover` cannot fire).
const PlayBtn = styled.button<{ $hovered: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2em;
  height: 2em;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  flex: 0 0 auto;
  opacity: ${({ $hovered }) => ($hovered ? 1 : 0.85)};
  transition: opacity 150ms ease-out;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

// Wrapper for the speaker button + horizontal volume rail. Always-visible
// pair — no hover-reveal dance, since the slider is part of the standard
// chrome and the user expects to see the volume position at a glance.
const VolumeWrap = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
`;

const VolumeBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  opacity: 0.95;
  transition: opacity 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;

// Horizontal volume rail — always visible, drag-to-set local state. The
// renderer doesn't expose a real volume API, so this updates only the
// visual state. Default position is fully on (slider full) to match
// YouTube's "ready to play with sound" first-load posture.
const VolumeRail = styled.div`
  position: relative;
  width: 6em;
  height: 0.3em;
  margin-left: 0.6em;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 0.15em;
  pointer-events: auto;
  cursor: pointer;
`;

const VolumeFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 0.15em;
`;

const VolumeThumb = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1em;
  height: 1em;
  background: #ffffff;
  border-radius: 50%;
`;

const Time = styled.span`
  font-size: 1em;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  margin-left: 0.4em;
  white-space: nowrap;
`;

// Right cluster — `margin-left: auto` pushes everything from autoplay
// onward to the right edge.
const RightCluster = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 1em;
`;

// Autoplay pill — a small toggle switch. Visual-only. When `on`, the track
// fills white and the thumb sits on the right; when `off`, the track is
// dim and the thumb sits on the left.
const AutoplayTrack = styled.button<{ $on: boolean }>`
  pointer-events: auto;
  position: relative;
  flex: 0 0 auto;
  width: 2.4em;
  height: 1.1em;
  border-radius: 1em;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  background: ${({ $on }) => ($on ? '#ffffff' : 'rgba(255, 255, 255, 0.35)')};
  transition: background 150ms ease-out;
`;

const AutoplayThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ $on }) => ($on ? 'calc(100% - 0.95em)' : '0.15em')};
  transform: translateY(-50%);
  width: 0.8em;
  height: 0.8em;
  border-radius: 50%;
  background: ${({ $on }) => ($on ? '#000000' : '#ffffff')};
  transition:
    left 150ms ease-out,
    background 150ms ease-out;
`;

// CC button — `aria-pressed` mirrors `showCaptions`. Active state paints a
// filled white pill (the button's own `border-radius` shapes the silhouette
// behind the icon) with the glyph flipped to dark so it reads against the
// pill. The icon keeps its own rounded-rect outline as a subtle border on
// top of the pill — both states use the same icon component.
const CCButton = styled.button<{ $active: boolean }>`
  pointer-events: auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  border: none;
  border-radius: 0.4em;
  padding: 0.15em 0.25em;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
  transition:
    opacity 150ms ease-out,
    background 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;

// Generic icon-button styling for the visual-only right-cluster controls
// (settings, miniplayer, theater, fullscreen).
const ControlIconBtn = styled.span`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: #ffffff;
  opacity: 0.85;
  cursor: pointer;
  transition: opacity 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;
